import { Place, GpxPoint, ParsedGpxData, MapProps } from "../../types/types";
import type { LatLngExpression } from "leaflet";
import StrictGpxParser from "../../lib/strictGpxParser";
import { getPlacesByLocation } from "../../services/getPlacesByLocation";
import { avgRouteCoords } from "../../utils/centerCoordinates";

export async function fetchGpx(url: string): Promise<string> {
  const response = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
  if (!response.ok) throw new Error(`Failed to fetch GPX: ${response.status}`);
  return response.text();
}

export function extractPolylineFromGpx(
  gpxData: ParsedGpxData,
): Array<LatLngExpression> {
  const polyline: Array<GpxPoint> = [];

  if (gpxData.tracks && gpxData.tracks.length > 0) {
    gpxData.tracks[0].points.forEach((point: GpxPoint): void => {
      polyline.push(point);
    });
  }

  return polyline.map((p: GpxPoint): { lat: number; lng: number } => ({
    lat: p.lat,
    lng: p.lon,
  }));
}

export function getRouteAndCenter(
  data: string,
  isMobile: boolean,
): { routeLine: Array<LatLngExpression>; mapCenter: GpxPoint } {
  const gpxObject = new StrictGpxParser();
  gpxObject.parse(data);
  const routeLine = extractPolylineFromGpx(gpxObject as unknown as ParsedGpxData);
  const mapCenter = avgRouteCoords(isMobile, routeLine);
  return { routeLine, mapCenter };
}

export function convertToGpxPoints(
  polyline: Array<LatLngExpression>,
): Array<GpxPoint> {
  return polyline.map((p: any) => ({ lat: p.lat, lon: p.lng }));
}

export function getFourRandomPoints(
  polyline: Array<LatLngExpression>,
): Array<LatLngExpression> {
  if (polyline.length < 4) {
    return polyline;
  }

  const randomPoints: Array<LatLngExpression> = [];
  const usedIndices = new Set<number>();

  while (randomPoints.length < 4) {
    const randomIndex = Math.floor(Math.random() * polyline.length);
    if (!usedIndices.has(randomIndex)) {
      usedIndices.add(randomIndex);
      randomPoints.push(polyline[randomIndex]);
    }
  }

  return randomPoints;
}

export async function fetchPlacesForRoute(
  polyline: Array<LatLngExpression>,
): Promise<Array<Place>> {
  const randomPoints = getFourRandomPoints(polyline);
  const placePromises = randomPoints.map(
    (p: LatLngExpression): Promise<Array<Place>> => getPlacesByLocation(p),
  );

  const placesArrays = await Promise.all(placePromises);
  return placesArrays.flat();
}
